import React from 'react';
import {findDOMNode} from 'react-dom';
import immutable from 'immutable';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';
import RenderToLayer from 'material-ui/internal/RenderToLayer';
import 'styles/utils/Tooltip.styl';

/**
 * Higher order component over https://github.com/wwayne/react-tooltip
 *
 * This component accepts all props from the react-tooltip library, plus the following:
 *   - renderToLayer (Boolean) Renders tooltip to a top-level div if true
 *   - active (Boolean) Programmatically set if tooltip is visible
 *   - parent (Object) React ref of the parent component, needed if using 'active'
 *   - getTooltipRef (Function) called with the tooltip ref
 *
 * It also adds a base class name for all tooltips
 */
export default class Tooltip extends React.Component {
  componentDidUpdate(prevProps) {
    const activeDefined = typeof this.props.active !== 'undefined';
    const activeChanged = activeDefined && this.props.active !== prevProps.active;
    const isTooltipActive = this.props.active;

    const oldOffset = prevProps.offset;
    const newOffset = this.props.offset;
    const offsetChanged = !immutable.Map(oldOffset).equals(immutable.Map(newOffset));

    const tooltipShouldHide = activeChanged && !isTooltipActive;
    const tooltipShouldShow = activeChanged && isTooltipActive;
    const tooltipShouldUpdate = offsetChanged && isTooltipActive;

    // update tooltip component's internal state, note that this needs the reference of the
    // parent component (the one with the 'data-for' attribute matching the tooltip)
    const tooltipParentDiv = findDOMNode(this.props.parent) ? this.props.parent : null;
    if (tooltipParentDiv) {
      if (tooltipShouldHide) {
        // we set an interval here to avoid conflict with a previous 'ReactTooltip.show' that
        // hasn't executed yet.
        setImmediate(() => {
          ReactTooltip.hide(tooltipParentDiv);
        });
      } else if (tooltipShouldShow || tooltipShouldUpdate) {
        // we need to hide the tooltip before showing it
        ReactTooltip.hide(tooltipParentDiv);
        setImmediate(() => {
          ReactTooltip.show(tooltipParentDiv);
        });
      }
    }

    // ...i dont know.
    const {prePop} = this.props;
    if (prePop)
      setImmediate(() => {
        this.props.onMouseEnterTooltip();
      });
  }

  handleTooltipRef = c => {
    if (this.props.getTooltipRef) {
      this.props.getTooltipRef(c);
    }
  };

  renderTooltip(tooltipProps) {
    const customEventProps = ['event', 'eventOff'];
    const customEventPropsDefined = customEventProps.some(key =>
      Object.keys(tooltipProps).includes(key)
    );
    const isFloat = tooltipProps.effect === 'float';

    // Hack that allows to update an already visible tooltip with new props.
    //
    // ReactTooltip keeps an internal state based on the props it received. This
    // state is only updated when calling its showTooltip() method.
    //
    // showTooltip() is called by triggering a 'show' event with ReactTooltip.show(), but
    // this only works if the tooltip was hidden, so we need to trigger a 'hide' event with
    // ReactTooltip.hide() first.
    //
    // The only remaining problem, which this hack addresses, is that mousemove is bound
    // to updateTooltip(), which unhides the tooltip, and this conflicts with the workflow.
    // We need to unbind mousemove, and this can only be done by defining a fake custom event.
    //
    // Defining a fake custom event also unbinds mouseeneter & mouseleave. So we need to manually
    // show or hide the tooltip by using the 'active' prop.
    //
    // Note: We only do this if the tooltip effect is not 'float', otherwise the mousemove event
    // is necessary to recalculate the tooltip position.
    if (!isFloat && !customEventPropsDefined) {
      tooltipProps['event'] = 'FAKE-EVENT';
      tooltipProps['eventOff'] = 'FAKE-EVENT';
    }
    return (
      <div
        className="tooltip-wrapper"
        onMouseEnter={tooltipProps.onMouseEnterTooltip}
        onMouseLeave={tooltipProps.onMouseLeaveTooltip}>
        <ReactTooltip
          {...tooltipProps}
          ref={this.handleTooltipRef}
          afterHide={() => {
            // tooltip keeps hiding on scroll or other random events even
            // though it is supposed to be active. if it tries to
            // hide itself, stop that.
            if (this.props.active) {
              ReactTooltip.show(findDOMNode(this.props.parent));
            }
          }}>
          {this.props.children}
        </ReactTooltip>
      </div>
    );
  }

  render() {
    let {renderToLayer, ...tooltipProps} = this.props;

    // add custom class name to all tooltips
    tooltipProps['class'] = classNames('base-tooltip', tooltipProps['class']);

    // tooltip can be rendered to a top-level div or directly
    if (renderToLayer) {
      return (
        <RenderToLayer
          open={true}
          useLayerForClickAway={false}
          render={() => this.renderTooltip(tooltipProps)}
        />
      );
    } else {
      return this.renderTooltip(tooltipProps);
    }
  }
}
